import { LngLatBounds, LngLatLike, Marker } from "mapbox-gl";
import { create } from "zustand";
import { SearchResult } from "../../client";
import { FilterSet, SearchSet, CustomMarker, Profile } from "../../types/types";
import {
  defaultSelectedFilters,
  defaultSelectedFeatures,
  writeToLog,
} from "../../const";
import { shallow } from 'zustand/shallow';

interface AppState {
  lng: number | undefined;
  lat: number | undefined;
  zoom: number;
  filterSet: FilterSet;
  isSearching: boolean;
  searchSet: SearchSet;
  searchResults: SearchResult[];
  unfilteredSearchResults: SearchResult[];
  isAllFiltersDialogOpen: boolean;
  permissionStatus: PermissionState | null;
  isMapInit: boolean;
  isLocationDialogOpen: boolean;
  selectedFeature: any;
  // markers: CustomMarker[] | undefined;
  markersRef : { current: CustomMarker[] };
  showRatingMarkers: boolean;
  loadingMore: boolean;
  isSettingsDialogOpen: boolean;
  isProfileDialogOpen: boolean;
  isHelpDialogOpen: boolean;
  isWelcomeModalOpen: boolean | null;
  isBetaModalOpen: boolean | null;
  isNoResultsModalOpen: boolean | null;
  domReady: boolean;
  profile: Profile;
  skipPermissionsCheck: boolean;
  isSearchDialogOpen: boolean;
  isResultsDialogOpen: boolean;
  resultsPosition: "collapsed" | "middle" | "expanded";
  fakeSearchInputValue: string;
  hideSearchThisAreaButton: boolean;
  hideMap: boolean;
  resultsPageNumber: number;
  hasMoreResults: boolean;
  previousZoom: number;
  poiCategories: any[] | undefined;
  lastSearchType: "zoom" | "initial" | "page" | "searchThisArea" | undefined;
  initialSearchViewportBounds: LngLatBounds | undefined;
  triggerLeftDockClose: boolean;
  zoomToSearchRunning: boolean;
  autoCenter: boolean;
  movingAutomatically: boolean;
  flyingTo: boolean;
  isSmallScreen: boolean;
  breakpoint: "xxxs" | "xxs" | "xss" | "xs" | "sm" | "md" | "lg" | "xl";
  drawerContentLoaded: boolean;
  databaseLocation: any;
  allCertified: boolean;
  newMarker: Marker | null;
  userMarker: Marker | null;
  lastMoveAction: "zoomToSearch" | "searchThisArea" | "none";
  initialZoomLevel: number | null;
  hasUserMovedMap: boolean;
  initialRRLngLat: LngLatLike | null;
  lastRRLngLat: LngLatLike | null;
  accessToken: string | null;
  accessTokenExp: string | null;
  preloadedGeoJson: any | null;
  isRunningInWebView: boolean;
  activeListUuid: string | null;

  // Actions to update state
  setLng: (value: number | undefined) => void;
  setLat: (value: number | undefined) => void;
  setZoom: (value: number) => void;
  setFilterSet: (value: FilterSet) => void;
  setIsSearching: (value: boolean) => void;
  setSearchSet: (value: SearchSet) => void;
  setSearchResults: (value: SearchResult[]) => void;
  setUnfilteredSearchResults: (value: SearchResult[]) => void;
  setAllFiltersDialogOpen: (value: boolean) => void;
  setPermissionStatus: (value: PermissionState | null) => void;
  setIsMapInit: (value: boolean) => void;
  setIsLocationDialogOpen: (value: boolean) => void;
  setSelectedFeature: (value: any) => void;
  setMarkersRef: (markers: CustomMarker[]) => void;
  // setMarkers: (value: CustomMarker[] | undefined) => void;
  setShowRatingMarkers: (value: boolean) => void;
  setLoadingMore: (value: boolean) => void;
  setIsSettingsDialogOpen: (value: boolean) => void;
  setIsProfileDialogOpen: (value: boolean) => void;
  setIsHelpDialogOpen: (value: boolean) => void;
  setIsWelcomeModalOpen: (value: boolean | null) => void;
  setIsBetaModalOpen: (value: boolean | null) => void;
  setIsNoResultsModalOpen: (value: boolean | null) => void;
  setDomReady: (value: boolean) => void;
  setProfile: (value: Profile) => void;
  setSkipPermissionsCheck: (value: boolean) => void;
  setIsSearchDialogOpen: (value: boolean) => void;
  setIsResultsDialogOpen: (value: boolean) => void;
  setResultsPosition: (value: "collapsed" | "middle" | "expanded") => void;
  setFakeSearchInputValue: (value: string) => void;
  setHideSearchThisAreaButton: (value: boolean) => void;
  setHideMap: (value: boolean) => void;
  setResultsPageNumber: (value: number) => void;
  setHasMoreResults: (value: boolean) => void;
  setPreviousZoom: (value: number) => void;
  setPoiCategories: (value: any[] | undefined) => void;
  setLastSearchType: (
    value: "zoom" | "initial" | "page" | "searchThisArea" | undefined
  ) => void;
  setInitialSearchViewportBounds: (value: LngLatBounds | undefined) => void;
  setTriggerLeftDockClose: (value: boolean) => void;
  setZoomToSearchRunning: (value: boolean) => void;
  setAutoCenter: (value: boolean) => void;
  setMovingAutomatically: (value: boolean) => void;
  setFlyingTo: (value: boolean) => void;
  setIsSmallScreen: (value: boolean) => void;
  setBreakpoint: (
    value: "xxxs" | "xxs" | "xss" | "xs" | "sm" | "md" | "lg" | "xl"
  ) => void;
  setDrawerContentLoaded: (value: boolean) => void;
  setDatabaseLocation: (value: any) => void;
  setAllCertified: (value: boolean) => void;
  setNewMarker: (value: Marker | null) => void;
  setUserMarker: (value: Marker | null) => void;
  setLastMoveAction: (
    value: "zoomToSearch" | "searchThisArea" | "none"
  ) => void;
  setInitialZoomLevel: (value: number | null) => void;
  setHasUserMovedMap: (value: boolean) => void;
  setInitialRRLngLat: (value: LngLatLike) => void;
  setLastRRLngLat: (value: LngLatLike) => void;
  setAccessToken: (value: string) => void;
  setAccessTokenExp: (value: string) => void;
  setPartial: <T extends Partial<AppState>>(partial: T) => void;
  setPreloadedGeoJson: (value: any) => void;
  setIsRunningInWebView: (value: boolean) => void;
  setActiveListUuid: (value: string) => void;
}

export const useAppState = create<AppState>((set, get) => ({
  lng: undefined,
  lat: undefined,
  zoom: 0,
  filterSet: {
    selectedFilters: defaultSelectedFilters,
    selectedFeatures: defaultSelectedFeatures,
  },
  isSearching: false,
  searchSet: {},
  searchResults: [],
  unfilteredSearchResults: [],
  isAllFiltersDialogOpen: false,
  permissionStatus: null,
  isMapInit: false,
  isLocationDialogOpen: false,
  selectedFeature: null,
  // markers: undefined,
  showRatingMarkers: true,
  loadingMore: false,
  isSettingsDialogOpen: false,
  isProfileDialogOpen: false,
  isHelpDialogOpen: false,
  isWelcomeModalOpen: null,
  isBetaModalOpen: null,
  isNoResultsModalOpen: null,
  domReady: false,
  profile: {},
  skipPermissionsCheck: false,
  isSearchDialogOpen: false,
  isResultsDialogOpen: false,
  resultsPosition: "middle" as "collapsed" | "middle" | "expanded",
  fakeSearchInputValue: "",
  hideSearchThisAreaButton: false,
  hideMap: false,
  resultsPageNumber: 1,
  hasMoreResults: false,
  previousZoom: 0,
  poiCategories: undefined,
  lastSearchType: undefined,
  initialSearchViewportBounds: undefined,
  triggerLeftDockClose: false,
  zoomToSearchRunning: false,
  autoCenter: false,
  movingAutomatically: true,
  flyingTo: false,
  isSmallScreen: false,
  breakpoint: "md" as "xxxs" | "xxs" | "xss" | "xs" | "sm" | "md" | "lg" | "xl",
  drawerContentLoaded: false,
  databaseLocation: {},
  allCertified: false,
  newMarker: null,
  userMarker: null,
  lastMoveAction: "none",
  initialZoomLevel: null,
  hasUserMovedMap: false,
  initialRRLngLat: null,
  lastRRLngLat: null,
  accessToken: null,
  accessTokenExp: null,
  preloadedGeoJson: null,
  isRunningInWebView: false,
  activeListUuid: null,
  markersRef: { current: [] },

  setLng: (value) => {
    if (get().lng !== value) {
      writeToLog(["[AppState]", "setLng", value]);
      set({ lng: value });
    }
  },
  setLat: (value) => {
    if (get().lat !== value) {
      writeToLog(["[AppState]", "setLat", value]);
      set({ lat: value });
    }
  },
  setZoom: (value) => {
    if (get().zoom !== value) {
      writeToLog(["[AppState]", "setZoom", value]);
      set({ zoom: value });
    }
  },
  setFilterSet: (value) => {
    if (get().filterSet !== value) {
      writeToLog(["[AppState]", "setFilterSet", value]);
      set({ filterSet: value });
    }
  },
  setIsSearching: (value) => {
    if (get().isSearching !== value) {
      writeToLog(["[AppState]", "setIsSearching", value]);
      set({ isSearching: value });
    }
  },
  setSearchSet: (value) => {
    const current = get().searchSet;
    if (!shallow(current, value)) {
    // if (get().searchSet !== value) {
      writeToLog(["[AppState]", "setSearchSet", value]);
      set({ searchSet: value });
    }
  },
  setSearchResults: (value) => {
    if (get().searchResults !== value) {
      writeToLog(["[AppState]", "setSearchResults", value]);
      set({ searchResults: value });
    }
  },
  setUnfilteredSearchResults: (value) => {
    if (get().unfilteredSearchResults !== value) {
      writeToLog(["[AppState]", "setUnfilteredSearchResults", value]);
      //remove dups
      let tempSearchResults = value.filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.locationId === item.locationId)
      );

      // reorder the results
      tempSearchResults = tempSearchResults.sort((a, b) => {
        // 1. Sort by isPromoted (true first)
        if (a.isPromoted !== b.isPromoted) {
          return a.isPromoted ? -1 : 1;
        }

        if (a.ratings) {
          let aRating =
            (a.ratings?.find((r: any) => r.who === "All perspectives") as any)
              ?.rating ?? 0;
          let bRating =
            (b.ratings?.find((r: any) => r.who === "All perspectives") as any)
              ?.rating ?? 0;

          if (typeof aRating === "string") {
            aRating = 0;
          }

          if (typeof bRating === "string") {
            bRating = 0;
          }

          // 2. Within isPromoted, sort by rating (highest to lowest)
          if (aRating !== bRating) {
            return bRating - aRating;
          }
        }

        // 3. If ratings are the same, sort by isCertified (true first)
        if (a.isCertified !== b.isCertified) {
          return a.isCertified ? -1 : 1;
        }

        // 4. Finally, push items where isShaneLocation is false to the end
        if (a.isShaneLocation !== b.isShaneLocation) {
          return a.isShaneLocation ? -1 : 1;
        }

        return 0; // No difference, keep order
      });
      set({ unfilteredSearchResults: tempSearchResults });
    }
  },
  setAllFiltersDialogOpen: (value) => {
    if (get().isAllFiltersDialogOpen !== value) {
      writeToLog(["[AppState]", "setAllFiltersDialogOpen", value]);
      set({ isAllFiltersDialogOpen: value });
    }
  },
  setPermissionStatus: (value) => {
    if (get().permissionStatus !== value) {
      writeToLog(["[AppState]", "setPermissionStatus", value]);
      set({ permissionStatus: value });
    }
  },
  setIsMapInit: (value) => {
    if (get().isMapInit !== value) {
      writeToLog(["[AppState]", "setIsMapInit", value]);
      set({ isMapInit: value });
    }
  },
  setIsLocationDialogOpen: (value) => {
    if (get().isLocationDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsLocationDialogOpen", value]);
      set({ isLocationDialogOpen: value });
    }
  },
  setSelectedFeature: (value) => {
    if (get().selectedFeature !== value) {
      writeToLog(["[AppState]", "setSelectedFeature", value]);
      set({ selectedFeature: value });
    }
  },
  // setMarkers: (value) => {
  //   if (get().markers !== value) {
  //     writeToLog(["[AppState]", "setMarkers", value]);
  //     set({ markers: value });
  //   }
  // },
  setMarkersRef: (markers) =>
    set((state) => {
      state.markersRef.current = markers;
      return { markersRef: state.markersRef };
    }),
  setShowRatingMarkers: (value) => {
    if (get().showRatingMarkers !== value) {
      writeToLog(["[AppState]", "setShowRatingMarkers", value]);
      set({ showRatingMarkers: value });
    }
  },
  setLoadingMore: (value) => {
    if (get().loadingMore !== value) {
      writeToLog(["[AppState]", "setLoadingMore", value]);
      set({ loadingMore: value });
    }
  },
  setIsSettingsDialogOpen: (value) => {
    if (get().isSettingsDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsSettingsDialogOpen", value]);
      set({ isSettingsDialogOpen: value });
    }
  },
  setIsProfileDialogOpen: (value) => {
    if (get().isProfileDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsProfileDialogOpen", value]);
      set({ isProfileDialogOpen: value });
    }
  },
  setIsHelpDialogOpen: (value) => {
    if (get().isHelpDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsHelpDialogOpen", value]);
      set({ isHelpDialogOpen: value });
    }
  },
  setIsWelcomeModalOpen: (value) => {
    if (get().isWelcomeModalOpen !== value) {
      writeToLog(["[AppState]", "setIsWelcomeModalOpen", value]);
      set({ isWelcomeModalOpen: value });
    }
  },
  setIsBetaModalOpen: (value) => {
    if (get().isBetaModalOpen !== value) {
      writeToLog(["[AppState]", "setIsBetaModalOpen", value]);
      set({ isBetaModalOpen: value });
    }
  },
  setIsNoResultsModalOpen: (value) => {
    if (get().isNoResultsModalOpen !== value) {
      writeToLog(["[AppState]", "setIsNoResultsModalOpen", value]);
      set({ isNoResultsModalOpen: value });
    }
  },
  setDomReady: (value) => {
    if (get().domReady !== value) {
      writeToLog(["[AppState]", "setDomReady", value]);
      set({ domReady: value });
    }
  },
  setProfile: (value) => {
    if (get().profile !== value) {
      writeToLog(["[AppState]", "setProfile", value]);
      set({ profile: value });
    }
  },
  setSkipPermissionsCheck: (value) => {
    if (get().skipPermissionsCheck !== value) {
      writeToLog(["[AppState]", "setSkipPermissionsCheck", value]);
      set({ skipPermissionsCheck: value });
    }
  },
  setIsSearchDialogOpen: (value) => {
    if (get().isSearchDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsSearchDialogOpen", value]);
      set({ isSearchDialogOpen: value });
    }
  },
  setIsResultsDialogOpen: (value) => {
    if (get().isResultsDialogOpen !== value) {
      writeToLog(["[AppState]", "setIsResultsDialogOpen", value]);
      set({ isResultsDialogOpen: value });
    }
  },
  setResultsPosition: (value) => {
    if (get().resultsPosition !== value) {
      writeToLog(["[AppState]", "setResultsPosition", value]);
      set({ resultsPosition: value });
    }
  },
  setFakeSearchInputValue: (value) => {
    if (get().fakeSearchInputValue !== value) {
      writeToLog(["[AppState]", "setFakeSearchInputValue", value]);
      set({
        fakeSearchInputValue: (value ?? "")
          .replaceAll(" ()", "")
          .replaceAll("()", ""),
      });
    }
  },
  setHideSearchThisAreaButton: (value) => {
    if (get().hideSearchThisAreaButton !== value) {
      writeToLog(["[AppState]", "setHideSearchThisAreaButton", value]);
      set({ hideSearchThisAreaButton: value });
    }
  },
  setHideMap: (value) => {
    if (get().hideMap !== value) {
      writeToLog(["[AppState]", "setHideMap", value]);
      set({ hideMap: value });
    }
  },
  setResultsPageNumber: (value) => {
    if (get().resultsPageNumber !== value) {
      writeToLog(["[AppState]", "setResultsPageNumber", value]);
      set({ resultsPageNumber: value });
    }
  },
  setHasMoreResults: (value) => {
    if (get().hasMoreResults !== value) {
      writeToLog(["[AppState]", "setHasMoreResults", value]);
      set({ hasMoreResults: value });
    }
  },
  setPreviousZoom: (value) => {
    if (get().previousZoom !== value) {
      writeToLog(["[AppState]", "setPreviousZoom", value]);
      set({ previousZoom: value });
    }
  },
  setPoiCategories: (value) => {
    if (get().poiCategories !== value) {
      writeToLog(["[AppState]", "setPoiCategories", value]);
      set({ poiCategories: value });
    }
  },
  setLastSearchType: (value) => {
    if (get().lastSearchType !== value) {
      writeToLog(["[AppState]", "setLastSearchType", value]);
      set({ lastSearchType: value });
    }
  },
  setInitialSearchViewportBounds: (value) => {
    if (get().initialSearchViewportBounds !== value) {
      writeToLog(["[AppState]", "setInitialSearchViewportBounds", value]);
      set({ initialSearchViewportBounds: value });
    }
  },
  setTriggerLeftDockClose: (value) => {
    if (get().triggerLeftDockClose !== value) {
      writeToLog(["[AppState]", "setTriggerLeftDockClose", value]);
      set({ triggerLeftDockClose: value });
    }
  },
  setZoomToSearchRunning: (value) => {
    if (get().zoomToSearchRunning !== value) {
      writeToLog(["[AppState]", "setZoomToSearchRunning", value]);
      set({ zoomToSearchRunning: value });
    }
  },
  setAutoCenter: (value) => {
    if (get().autoCenter !== value) {
      writeToLog(["[AppState]", "setAutoCenter", value]);
      set({ autoCenter: value });
    }
  },
  setMovingAutomatically: (value) => {
    if (get().movingAutomatically !== value) {
      writeToLog(["[AppState]", "setMovingAutomatically", value]);
      set({ movingAutomatically: value });
    }
  },
  setFlyingTo: (value) => {
    if (get().flyingTo !== value) {
      writeToLog(["[AppState]", "setFlyingTo", value]);
      set({ flyingTo: value });
    }
  },
  setIsSmallScreen: (value) => {
    if (get().isSmallScreen !== value) {
      writeToLog(["[AppState]", "setIsSmallScreen", value]);
      set({ isSmallScreen: value });
    }
  },
  setBreakpoint: (value) => {
    if (get().breakpoint !== value) {
      writeToLog(["[AppState]", "setBreakpoint", value]);
      set({ breakpoint: value });
    }
  },
  setDrawerContentLoaded: (value) => {
    if (get().drawerContentLoaded !== value) {
      writeToLog(["[AppState]", "setDrawerContentLoaded", value]);
      set({ drawerContentLoaded: value });
    }
  },
  setDatabaseLocation: (value) => {
    if (get().databaseLocation !== value) {
      writeToLog(["[AppState]", "setDatabaseLocation", value]);
      set({ databaseLocation: value });
    }
  },
  setAllCertified: (value) => {
    if (get().allCertified !== value) {
      writeToLog(["[AppState]", "setAllCertified", value]);
      set({ allCertified: value });
    }
  },
  setNewMarker: (value) => {
    if (get().newMarker !== value) {
      writeToLog(["[AppState]", "setNewMarker", value]);
      set({ newMarker: value });
    }
  },
  setUserMarker: (value) => {
    if (get().userMarker !== value) {
      writeToLog(["[AppState]", "setUserMarker", value]);
      set({ userMarker: value });
    }
  },
  setLastMoveAction: (value) => {
    if (get().lastMoveAction !== value) {
      writeToLog(["[AppState]", "setLastMoveAction", value]);
      set({ lastMoveAction: value });
    }
  },
  setInitialZoomLevel: (value) => {
    if (get().initialZoomLevel !== value) {
      writeToLog(["[AppState]", "setInitialZoomLevel", value]);
      set({ initialZoomLevel: value });
    }
  },
  setHasUserMovedMap: (value) => {
    if (get().hasUserMovedMap !== value) {
      writeToLog(["[AppState]", "setHasUserMovedMap", value]);
      set({ hasUserMovedMap: value });
    }
  },
  setInitialRRLngLat: (value) => {
    if (get().initialRRLngLat !== value) {
      writeToLog(["[AppState]", "setInitialRRLngLat", value]);
      set({ initialRRLngLat: value });
    }
  },
  setLastRRLngLat: (value) => {
    if (get().lastRRLngLat !== value) {
      writeToLog(["[AppState]", "setLastRRLngLat", value]);
      set({ lastRRLngLat: value });
    }
  },
  setAccessToken: (value) => {
    if (get().accessToken !== value) {
      writeToLog(["[AppState]", "setAccessToken", value]);
      set({ accessToken: value });
    }
  },
  setAccessTokenExp: (value) => {
    if (get().accessTokenExp !== value) {
      writeToLog(["[AppState]", "setAccessTokenExp", value]);
      set({ accessTokenExp: value });
    }
  },
  setPartial: (partial) => {
    writeToLog(["[AppState]", "setPartial", partial]);
    set(partial);
  },
  setPreloadedGeoJson: (value) => {
    // const current = get().preloadedGeoJson;
    // if (!shallow(current, value)) {
      writeToLog(["[AppState]", "setPreloadedGeoJson", value]);
      set({ preloadedGeoJson: value });
    // }

  },
  setIsRunningInWebView: (value) => {
    if (get().isRunningInWebView !== value) {
      writeToLog(["[AppState]", "setIsRunningInWebView", value]);
      set({ isRunningInWebView: value });
    }
  },
  setActiveListUuid: (value) => {
    if (get().activeListUuid !== value) {
      writeToLog(["[AppState]", "setActiveListUuid", value]);
      set({ activeListUuid: value });
    }
  },
}));
